import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  DisplayText,
  Modal,
  ScrollViewWithHeaderTitle,
  theme,
  useResponsive,
  useSafeExpoNavigation,
  useSafeNavigation,
  useSafeRouteParams,
} from '@sg/garnish';
import type { LineItem, LineItemSuccess } from '@sg/graphql-schema';

import { useLineItemName } from '@order/shared/hooks';

import { AppFooter, LoadingAnimation } from '../../components';
import { ErrorFallback } from '../../components/ErrorBoundary';
import { SharedLineItem } from '../../components/SharedLineItem';
import { useReorderLineItem } from '../ReorderingScreen/hooks';
import { useGetSharedLineItemQuery } from './GraphQL/ShareLineItemScreen.generated';

export const ShareLineItemScreen = () => {
  const { formatMessage } = useIntl();
  const { lineItemSlug } = useSafeRouteParams<{ lineItemSlug: string }>();

  // ─────────── Queries ──────────
  const [{ fetching, data }] = useGetSharedLineItemQuery({
    variables: { input: { slug: lineItemSlug } },
  });

  // ─────────── Data ─────────────
  const lineItemBySlug = data?.lineItemBySlug as LineItemSuccess;
  const error = formatMessage(messages.shareLineItemError);

  // ────── Render Control ────────
  if (fetching) return <LoadingAnimation />;

  if (!lineItemBySlug?.lineItem) return <ErrorFallback error={error} />;

  return <ShareLineItemContentScreen lineItem={lineItemBySlug.lineItem} />;
};

const ShareLineItemContentScreen = ({
  lineItem,
}: Readonly<{
  lineItem: LineItem;
}>) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const safeNav = useSafeNavigation();
  const expoNav = useSafeExpoNavigation();
  const setOptions = safeNav?.setOptions ?? expoNav?.setOptions;

  const startReorderLineItem = useReorderLineItem(lineItem);
  const productName = useLineItemName(lineItem.product, lineItem);

  React.useLayoutEffect(() => {
    setOptions?.({ title: productName });
  }, [setOptions, productName]);

  return (
    <ScrollViewWithHeaderTitle
      style={styles.body}
      contentContainerStyle={styles.grow}
    >
      <Modal.Row
        style={
          currentBreakpoint.isXS ? styles.headerMobile : styles.headerDesktop
        }
      >
        <DisplayText style={styles.title} size={3}>
          {productName}
        </DisplayText>
        <BodyText style={styles.customerName} size={3}>
          {formatMessage(messages.accountSharedBy, {
            name: lineItem.customerName,
          })}
        </BodyText>
      </Modal.Row>
      <Modal.Row
        style={[styles.content, currentBreakpoint.isXS && styles.contentMobile]}
      >
        <View style={!currentBreakpoint.isXS && styles.productDesktop}>
          <SharedLineItem
            lineItem={lineItem}
            onAddToBag={startReorderLineItem}
          />
        </View>
      </Modal.Row>
      <AppFooter />
    </ScrollViewWithHeaderTitle>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  grow: {
    flexGrow: 1,
  },
  body: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  headerDesktop: {
    paddingTop: theme.spacing['4'],
    alignItems: 'center',
  },
  headerMobile: {
    alignItems: 'flex-start',
  },
  title: {
    paddingTop: theme.spacing['6'],
  },
  customerName: {
    paddingTop: theme.spacing['3'],
  },
  content: {
    alignItems: 'center',
    paddingVertical: theme.spacing['10'],
  },
  contentMobile: {
    paddingVertical: theme.spacing['6'],
  },
  productDesktop: {
    maxWidth: 382,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  shareLineItemError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Share Line Item | Error',
  },
  accountSharedBy: {
    defaultMessage: 'By {name}',
    description: 'Account | Shared By',
  },
});
